<template>
  <span class="main-wrapper" @mouseenter="showTooltip" @mouseleave="hideTooltip" @click="clickTitle">
    <CitationInfoLabel v-if="display2ndOrderReferences" :label="label" :source="source" show-icon />
    <span v-else class="info-icon-black">{{ label }}</span>

    <InfoTooltipContent
      v-if="isTooltipVisible"
      :id="id"
      :title="title"
      :page="page"
      :snippet="snippet"
      :thumb-url="thumbUrl"
      :source="source"
      :reference="currentReference"
      @title-clicked="clickTitle"
      @preview="clickPreview"
      class="tooltip-container"
      :style="tooltipStyle"
    />
  </span>
</template>

<script>
import { computed, ref } from 'vue';

import { decodeFromBase64 } from '~/utils/message';
import CitationInfoLabel from '~/components/conversation/chatMessage/CitationInfoLabel.vue';
import InfoTooltipContent from '~/components/conversation/chatMessage/InfoTooltipContent.vue';
import useConversations from '~/composables/useConversations';

export default {
  name: 'InfoTooltip',
  components: { CitationInfoLabel, InfoTooltipContent },
  emits: ['titleClicked'],
  props: {
    id: {
      type: String,
      default: '',
    },
    snippetIndex: {
      type: String,
      default: '',
    },
    messageData: {
      type: Object,
      default: () => {},
    },
    page: {
      type: String,
      default: '',
    },
    label: {
      type: Number,
      default: 1,
    },
    onGetTooltipThumb: {
      type: Function,
      default: () => {},
    },
    tooltipStyle: {
      type: Object,
      default: () => {},
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    refNum: {
      type: Number,
      default: null,
    },
    referenceId: {
      type: String,
      default: '',
    },
  },

  setup(props, { emit }) {
    const isTooltipVisible = ref(false);
    const thumbUrl = ref('');
    const currentReference = ref({});

    const { display2ndOrderReferences } = useConversations();

    const findReference = () => {
      const messageBlock = props.messageData?.data?.[0] || {};
      const references = Array.isArray(messageBlock.references) ? messageBlock.references : [];
      const secondOrderReferences = Array.isArray(messageBlock.secondOrderReferences)
        ? messageBlock.secondOrderReferences
        : [];
      const citationKey = `${props.id}-${props.page}-${props.snippetIndex}`;
      const selectedSecondOrderRef =
        props.refNum != null
          ? secondOrderReferences.find((ref) => ref.referenceId === props.referenceId && ref.refNum === props.refNum)
          : null |
              secondOrderReferences.find((ref) => ref.citationId === citationKey || ref.citationKey === citationKey) ||
            secondOrderReferences.find((ref) => ref.externalId === props.id || ref.referenceId === props.id);

      if (selectedSecondOrderRef) {
        return selectedSecondOrderRef;
      }

      return references.find((ref) => ref.externalId === props.id) || {};
    };

    const currentIndex = computed(
      () => (props.messageData?.data?.[0]?.references || []).findIndex((ref) => ref.externalId === props.id) + 1,
    );

    const title = computed(() => currentReference.value?.name || '');

    const snippet = computed(() => {
      if (!currentReference.value?.snippets) {
        return '';
      }
      const specificSnippet = currentReference.value.snippets[`${props.page}-${props.snippetIndex}`];
      return '...' + decodeFromBase64(specificSnippet || currentReference.value?.snippet || '');
    });

    const loadPageCover = async () => {
      thumbUrl.value = await props.onGetTooltipThumb(props.id, props.page);
    };

    const showTooltip = async () => {
      if (display2ndOrderReferences.value) {
        currentReference.value = findReference();
      } else {
        currentReference.value = (props.messageData?.data?.[0]?.references || []).find(
          (ref) => ref.externalId === props.id,
        );
      }
      isTooltipVisible.value = true;
      await loadPageCover();
    };

    const hideTooltip = () => (isTooltipVisible.value = false);
    const clickTitle = () => emit('titleClicked', { reference: currentReference.value, page: Number(props.page) });
    const clickPreview = (page) => emit('titleClicked', { reference: currentReference.value, page: Number(page) });

    return {
      currentIndex,
      currentReference,
      clickPreview,
      clickTitle,
      thumbUrl,
      isTooltipVisible,
      showTooltip,
      hideTooltip,
      title,
      snippet,
      display2ndOrderReferences,
    };
  },
};
</script>

<style scoped lang="scss">
.main-wrapper {
  position: relative;

  .info-icon-black {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 16px;
    height: 16px;
    background-color: $bb-neutral-1;
    border-radius: 50%;
    color: $bb-neutral-0;
    box-sizing: border-box;
    margin-right: $bb-spacing-1;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px;

    &:hover {
      background-color: $bb-primary-3;
    }
  }

  .tooltip-container {
    position: absolute;
    left: 50%;
    transform: translateX(-10%);
    background-color: $bb-neutral-0;
    width: 400px;
    max-height: 432px;
    overflow: hidden;
    border: 1px solid $bb-neutral-3;
    border-radius: 4px;
    box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column; /* Stack rows */
    z-index: 1000;
  }
}
</style>
